import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import type { GridColDef, GridRowId, GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import GridInfoFooter from './GridInfoFooter';

interface SelectableCampaignsGridProps<TRow extends GridValidRowModel> {
  /** Rows currently visible (after name / "selected only" filters). */
  rows: TRow[];
  columns: GridColDef<TRow>[];
  getRowId: (row: TRow) => string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  limit?: number;
  isLoading: boolean;
  limitMessage?: string;
  footer?: ReactNode;
}

const SelectableCampaignsGrid = <TRow extends GridValidRowModel>({
  rows,
  columns,
  getRowId,
  selectedIds,
  onSelectionChange,
  limit,
  isLoading,
  limitMessage,
  footer,
}: SelectableCampaignsGridProps<TRow>) => {
  const isLimitReached = limit !== undefined && selectedIds.length >= limit;

  const visibleIds = useMemo(() => new Set(rows.map(getRowId)), [rows, getRowId]);
  const hiddenSelectedIds = useMemo(
    () => selectedIds.filter((id) => !visibleIds.has(id)),
    [selectedIds, visibleIds],
  );
  const safeSelectionModel = useMemo(
    () => selectedIds.filter((id) => visibleIds.has(id)),
    [selectedIds, visibleIds],
  );

  const handleSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      const visibleSelection = (limit !== undefined ? model.slice(0, limit) : model).map(String);
      const hiddenSet = new Set(hiddenSelectedIds);
      const merged = [
        ...hiddenSelectedIds,
        ...visibleSelection.filter((id) => !hiddenSet.has(id)),
      ];

      onSelectionChange(limit !== undefined ? merged.slice(0, limit) : merged);
    },
    [hiddenSelectedIds, limit, onSelectionChange],
  );

  const isRowSelectable = useCallback(
    (params: { id: GridRowId }) => !isLimitReached || selectedIds.includes(String(params.id)),
    [isLimitReached, selectedIds],
  );

  const footerComponent = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => {
      if (footer !== undefined) return <>{footer}</>;
      if (!selectedIds.length) return null;

      return isLimitReached ? (
        <GridInfoFooter showIcon message={limitMessage ?? `Up to ${limit} rows can be selected`} />
      ) : (
        <GridInfoFooter message={`${selectedIds.length} rows selected`} />
      );
    },
    [footer, selectedIds.length, isLimitReached, limitMessage, limit],
  );

  return (
    <Box sx={{ flex: 1, height: '100%', width: '100%', position: 'relative' }}>
      <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          rowSelectionModel={safeSelectionModel}
          loading={isLoading}
          onRowSelectionModelChange={handleSelectionModelChange}
          hideFooterPagination
          isRowSelectable={isRowSelectable}
          slots={{ footer: footerComponent }}
          getRowId={getRowId}
        />
      </Box>
    </Box>
  );
};

export default SelectableCampaignsGrid;
